import { useMemo } from "react"

import { DEFAULT_LANGUAGE, useLocale, useTranslation } from "@bounce/i18n"

import type { Interpolations } from "./useInterpolations"
import type { PoiData } from "@/libs/bounce/pois"
import { interpolateText } from "@/libs/prismic/helpers"

export type UseSEOOptions = {
  poi: Pick<PoiData, "splitTestData" | "metaTitle" | "metaDescription" | "mainStore">
  interpolations: Interpolations
}

export const useSEO = ({ poi, interpolations }: UseSEOOptions) => {
  const [t] = useTranslation("marketing-ls-poi")
  const { language } = useLocale()

  const metaTitle = useMemo(() => {
    const metaTitleOverride = poi.splitTestData.metaTitle

    if (metaTitleOverride) {
      return interpolateText(metaTitleOverride, interpolations)
    }

    let metaTitle = t(
      "page.place.metaTitleV10",
      "{{PLACE_NAME}} Luggage Storage & Lockers From {{PRICE}} / Day",
      interpolations,
    )

    if (language !== DEFAULT_LANGUAGE)
      metaTitle = t("page.place.metaTitleV8", "{{PLACE_NAME}} Luggage Storage From {{PRICE}} / Day", interpolations)

    if (metaTitle.length > 60)
      metaTitle = t("page.place.metaTitleV9", "{{PLACE_NAME}} Luggage Storage {{PRICE}} / Day", interpolations)

    return metaTitle
  }, [language, interpolations, poi.splitTestData.metaTitle, t])

  return {
    metaTitle: metaTitle,
    metaDescription: t(
      "page.place.metaDescriptionV4",
      "Best-rated {{PLACE_NAME}} luggage storage for only {{PRICE}}/ day. Booking includes {{INSURANCE_AMOUNT}} protection and free cancellation.",
      interpolations,
    ),
  }
}
